import React, { useState, useEffect, useCallback, useMemo } from "react";
import AdminNav from "../components/Admin/AdminNav";
import ProductEditModal from "../components/Admin/ProductEditModal";
import ImageWithPlaceholder from "../components/common/ImageWithPlaceholder";
import { API_URL, PRODUCTS_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./allProducts.module.css";

const formatNaira = (value) =>
  `₦${new Intl.NumberFormat("en-NG").format(value || 0)}`;

const GetAllItem = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const canManage = canManageProducts();

  const fetchProducts = useCallback(async () => {
    try {
      // ?all=1 with a staff token includes products hidden from the store
      const response = await fetch(`${PRODUCTS_URL}?all=1`, {
        headers: authHeader(),
      });
      const data = await response.json();
      if (response.ok && data.products) {
        const sorted = data.products.sort(
          (a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)
        );
        setProducts(sorted);
        setError("");
      } else {
        setProducts([]);
      }
    } catch (err) {
      setError("Could not load products — is the backend running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetch(`${API_URL}/categories`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => Array.isArray(list) && setCategories(list))
      .catch(() => {});
    fetch(`${API_URL}/collections`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => Array.isArray(list) && setCollections(list))
      .catch(() => {});
  }, [fetchProducts]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !q ||
        p.name?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q);
      const categoryId = p.category?._id || p.category;
      const matchesCategory = !categoryFilter || categoryId === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, categoryFilter]);

  const confirmDelete = async () => {
    const product = deleting;
    setDeleting(null);
    try {
      const response = await fetch(`${API_URL}/products/${product._id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Could not delete the product.");
      }
      flash(`“${product.name}” deleted.`);
      fetchProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaved = (name) => {
    setEditing(null);
    flash(`“${name}” updated.`);
    fetchProducts();
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>All Products</h1>
          <p className={styles.subtitle}>
            Everything currently in the store database.
          </p>
        </header>

        {error && <p className={styles.bannerError}>{error}</p>}
        {notice && <p className={styles.bannerSuccess}>{notice}</p>}

        <div className={styles.toolbar}>
          <input
            className={styles.search}
            placeholder="Search by name or brand…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={styles.filterSelect}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className={styles.count}>
            {visible.length} of {products.length}
          </span>
        </div>

        {loading ? (
          <p className={styles.loading}>Loading products…</p>
        ) : visible.length === 0 ? (
          <div className={styles.empty}>
            <p>No products match.</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {visible.map((product) => (
              <article key={product._id} className={styles.card}>
                <div className={styles.imageWrap}>
                  <div className={styles.badges}>
                    {product.active === false && (
                      <span className={styles.badgeHidden}>Hidden from store</span>
                    )}
                    {product.isFeatured && <span className={styles.badge}>Featured</span>}
                    {product.outOfStock && <span className={styles.badgeOut}>Out of stock</span>}
                  </div>
                  <ImageWithPlaceholder
                    src={product.image}
                    alt={product.name}
                    className={styles.image}
                    wrapperStyle={{ position: "absolute", inset: 0, display: "block" }}
                  />
                </div>
                <div className={styles.body}>
                  <h2 className={styles.name}>{product.name}</h2>
                  <div className={styles.meta}>
                    {product.productCollection?.name && (
                      <span>{product.productCollection.name}</span>
                    )}
                    {product.category?.name && <span>{product.category.name}</span>}
                    <span>{new Date(product.dateCreated).toLocaleDateString()}</span>
                  </div>
                  <p className={styles.price}>{formatNaira(product.price)}</p>
                  <span
                    className={
                      product.stockInCount > 3 ? styles.stock : styles.stockLow
                    }
                  >
                    {product.stockInCount ?? 0} in stock
                  </span>
                </div>
                {canManage && (
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => setEditing(product)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => setDeleting(product)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ProductEditModal
          product={editing}
          categories={categories}
          collections={collections}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {deleting && (
        <div className={styles.overlay} onClick={() => setDeleting(null)}>
          <div className={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.confirmTitle}>Delete product</h2>
            <p className={styles.confirmText}>
              Delete “{deleting.name}” from the store? This cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => setDeleting(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.dangerSolidBtn}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetAllItem;
